// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useRef, useEffect, useState, useCallback } from 'react';

// Minimal contentEditable-based rich text editor. Zero external dependencies.
// Uses the (deprecated but still universally supported) document.execCommand
// API for formatting. Output is HTML stored in `value` and propagated via
// `onChange(html)`. Trusted-author input — admins typing for the firm.
//
// Supported formatting: bold, italic, underline, H2, H3, paragraph, bullet
// list, numbered list, hyperlink, remove-format.

export default function RichTextEditor({ value, onChange, placeholder, minHeight = 220 }) {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Keep the editor DOM in sync with `value` prop, but ONLY when the editor
  // doesn't currently have focus. Re-setting innerHTML while the user is
  // typing collapses the selection to the start and feels broken.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = useCallback(() => {
    const el = editorRef.current;
    if (!el || !onChange) return;
    onChange(el.innerHTML);
  }, [onChange]);

  function exec(command, arg = null) {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    // eslint-disable-next-line no-restricted-syntax
    document.execCommand(command, false, arg);
    handleInput();
  }

  function insertLink() {
    const url = window.prompt('Enter URL (include https://):', 'https://');
    if (!url) return;
    exec('createLink', url);
    // Make the link open in new tab — execCommand can't set target,
    // so we do it manually on the most-recently-created anchor.
    const el = editorRef.current;
    if (el) {
      const anchors = el.querySelectorAll('a[href]');
      const last = anchors[anchors.length - 1];
      if (last) {
        last.setAttribute('target', '_blank');
        last.setAttribute('rel', 'noopener noreferrer');
        handleInput();
      }
    }
  }

  function clearFormatting() {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    // eslint-disable-next-line no-restricted-syntax
    document.execCommand('removeFormat', false, null);
    // eslint-disable-next-line no-restricted-syntax
    document.execCommand('formatBlock', false, 'P');
    handleInput();
  }

  return (
    <div className="rte" style={{
      border: `1px solid ${isFocused ? '#888' : '#ddd'}`,
      borderRadius: 4,
      background: '#fff',
      transition: 'border-color 0.15s',
    }}>
      {/* Toolbar */}
      <div className="rte-toolbar">
        <button type="button" className="rte-btn rte-bold" title="Bold (Ctrl+B)"
          onMouseDown={e => { e.preventDefault(); exec('bold'); }}>B</button>
        <button type="button" className="rte-btn rte-italic" title="Italic (Ctrl+I)"
          onMouseDown={e => { e.preventDefault(); exec('italic'); }}>I</button>
        <button type="button" className="rte-btn rte-underline" title="Underline (Ctrl+U)"
          onMouseDown={e => { e.preventDefault(); exec('underline'); }}>U</button>

        <span className="rte-sep" />

        <button type="button" className="rte-btn" title="Heading 2"
          onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'H2'); }}>H2</button>
        <button type="button" className="rte-btn" title="Heading 3"
          onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'H3'); }}>H3</button>
        <button type="button" className="rte-btn" title="Paragraph"
          onMouseDown={e => { e.preventDefault(); exec('formatBlock', 'P'); }}>P</button>

        <span className="rte-sep" />

        <button type="button" className="rte-btn" title="Bullet list"
          onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }}>• List</button>
        <button type="button" className="rte-btn" title="Numbered list"
          onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }}>1. List</button>

        <span className="rte-sep" />

        <button type="button" className="rte-btn" title="Insert link"
          onMouseDown={e => { e.preventDefault(); insertLink(); }}>Link</button>

        <span className="rte-sep" />

        <button type="button" className="rte-btn" title="Clear formatting"
          onMouseDown={e => { e.preventDefault(); clearFormatting(); }}>Clear</button>
      </div>

      {/* Editor surface */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-placeholder={placeholder || 'Start typing...'}
        className="rte-surface"
        style={{ minHeight }}
      />

      <style>{`
        .rte-toolbar {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 4px;
          padding: 6px;
          border-bottom: 1px solid #eee;
          background: #fafafa;
        }
        .rte-btn {
          height: 32px;
          min-width: 32px;
          padding: 0 10px;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 3px;
          cursor: pointer;
          font-family: Arial, sans-serif;
          font-size: 13px;
          color: #333;
          line-height: 1;
        }
        .rte-btn:hover { background: #f0f0f0; }
        .rte-btn:active { background: #e8e8e8; }
        .rte-bold { font-weight: 700; }
        .rte-italic { font-style: italic; }
        .rte-underline { text-decoration: underline; }
        .rte-sep {
          width: 1px;
          height: 22px;
          background: #ddd;
          margin: 0 4px;
          align-self: center;
          flex-shrink: 0;
        }
        .rte-surface {
          padding: 14px;
          outline: none;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 15px;
          line-height: 1.6;
          color: #0F1838;
          /* Important: prevent the editor from blowing past its container on
             mobile when an admin pastes long unbreakable strings (URLs etc). */
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .rte-surface[data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #aaa;
          pointer-events: none;
        }
        .rte-surface h2 { font-size: 20px; margin: 14px 0 8px; font-family: Georgia, serif; }
        .rte-surface h3 { font-size: 17px; margin: 12px 0 6px; font-family: Georgia, serif; }
        .rte-surface p  { margin: 0 0 10px; }
        .rte-surface ul, .rte-surface ol { margin: 0 0 10px 24px; }
        .rte-surface li { margin: 2px 0; }
        .rte-surface a  { color: #2f5496; text-decoration: underline; }

        /* Larger touch targets on mobile — keeps the toolbar usable on phones. */
        @media (max-width: 768px) {
          .rte-btn {
            height: 38px;
            min-width: 38px;
            padding: 0 12px;
            font-size: 14px;
          }
          .rte-toolbar { padding: 8px; gap: 6px; }
          .rte-sep { height: 26px; }
        }
      `}</style>
    </div>
  );
}
